import { format } from "date-fns";
import { enGB, arEG } from "date-fns/locale";

import store from "../store";
import { showHideSnackbar } from "../store/Snackbar/slice";
import messages from "../assets/locales/messages";
import { USERS_ROLES_TYPES, SIGNAL_VALUE_TYPE, USER_SEGMENTS } from "./Constants";

export const showSnackMsgFromLocal = (msg, type = "success") => {
  const lang = store.getState().locale.locale;
  const { successMsgs } = messages[lang];
  if (msg) {
    store.dispatch(
      showHideSnackbar({
        isOpen: true,
        type,
        message: successMsgs[msg],
      })
    );
  }
};

export const showSnackMsg = (message, type = "error") => {
  if (message) {
    store.dispatch(
      showHideSnackbar({
        isOpen: true,
        type,
        message,
      })
    );
  }
};

export const camelToSnakeCase = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export function debounce(func, wait, immediate) {
  let timeout;

  return (...args) => {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
}

export const formatDateLocalized = (date, locale) => {
  if (!date) return "__";
  return format(new Date(date), "dd MMMM yyyy", {
    locale: locale === "ar" ? arEG : enGB,
  });
};

export const formatDateShort = (date, locale) => {
  if (!date) return "__";
  return format(new Date(date), "dd-MM-yyyy", {
    locale: locale === "ar" ? arEG : enGB,
  });
};

export const formatTimeLocalized = (date, locale) => {
  if (!date) return "__";
  return format(new Date(date), "HH:mm a", {
    locale: locale === "ar" ? arEG : enGB,
  });
};

export const extractFileName = (filePath) => {
  // filePath can be:
  // - A string URL: https://exm-pv-staging-uploads.s3.amazonaws.com/Admin/offers_banners/1683719031847_photo-1628563694622-5a76957fd09c.jpeg
  // - A File object: { name: "file.jpg", ... }
  // - An object with url/image property: { url: "...", ... }
  // - null/undefined
  
  if (!filePath) {
    return "";
  }
  
  // If it's a File object, use the name property
  if (filePath instanceof File) {
    return filePath.name;
  }
  
  // If it's an object with a name property
  if (typeof filePath === 'object' && filePath !== null) {
    if (filePath.name) {
      return filePath.name;
    }
    // If it has url or image property, extract from that
    const urlString = filePath.url || filePath.image || filePath.download_link || filePath.src;
    if (urlString && typeof urlString === 'string') {
      return urlString.split("/")[urlString.split("/").length - 1];
    }
    return "";
  }
  
  // If it's a string, extract filename from URL
  if (typeof filePath === 'string') {
    return filePath.split("/")[filePath.split("/").length - 1];
  }
  
  return "";
};

// used to check if filters in a page has values except an array of keys (['page', 'query'] for example)
export const hasFiltersExcept = (params, except) => {
  for (let key of params.keys()) {
    if (!except.includes(key)) return true;
  }
  return false;
};

export const checkBackgroundImageValidity = (url, callback) => {
  const img = new Image();
  img.onload = function () {
    // Image loaded successfully
    callback(true);
  };
  img.onerror = function () {
    // Image failed to load
    callback(false);
  };
  img.src = url;
};

export  const isValidAnimation = (animationData) => {
  try {
      return animationData && 
             typeof animationData === 'object' && 
             'v' in animationData && 
             'fr' in animationData && 
             'layers' in animationData;
  } catch (error) {
      return false;
  }
};

export const handleRole = (previous_role_status, usersLocale) => {
  switch (previous_role_status) {
    case USERS_ROLES_TYPES.singleOwner:
      return usersLocale.singleOwner;
    case USERS_ROLES_TYPES.fleetOwner:
      return usersLocale.fleetOwner;
    case USERS_ROLES_TYPES.driver:
      return usersLocale.driver;
    default:
      return "";
  }
};

export const handleUserSegment = (segment, segmentsLocale) => {
  const key = Object.keys(USER_SEGMENTS).find(
    (segmentKey) => USER_SEGMENTS[segmentKey] === segment
  );
  return key ? segmentsLocale?.[key] : "";
};

// Derives the loyalty tier (basic/silver/gold/platinum) from a raw segment value (e.g. "cv_gold" -> "gold")
export const getSegmentTier = (segment) => {
  if (!segment) return null;
  if (segment.includes("platinum")) return "platinum";
  if (segment.includes("gold")) return "gold";
  if (segment.includes("silver")) return "silver";
  return "basic";
};

// Derive a sub-task's field behaviour from the BE challenge template object
export const getSignalTypeConfig = (template) => ({
  hasValue: template?.value_type === SIGNAL_VALUE_TYPE.numeric,
  hasFlag: !!template?.supports_during_promo,
  isPredefined:
    Array.isArray(template?.predefined_values) &&
    template.predefined_values.length > 0,
});
